import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { unstable_usePrompt, useLocation, useNavigate } from "react-router-dom";

import {
  ActionIcon,
  Box,
  Button,
  Divider,
  rem,
  ScrollArea,
  Text,
  Textarea,
  Title,
  VisuallyHidden
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconArrowLeft } from "@tabler/icons-react";

import { MAX_TEXT_LENGTH, MAX_VARCHAR_LENGTH } from "../../constants/constants.ts";
import { useFormValidation } from "../../hooks/useFormValidation.ts";
import { useFormatHtml } from "../../hooks/useFormatHtml.ts";
import { Template } from "../../models/Template.ts";
import { TemplateDto } from "../../models/TemplateDto.ts";
import { useTemplateMutation } from "../../mutations/useTemplateMutation.ts";
import classes from "../../styles/pages/TemplateEditionPage.module.scss";
import { CancelModal } from "../modals/CancelModal.tsx";
import { TemplatePreviewModal } from "../modals/TemplatePreviewModal.tsx";
import { TemplatedMailModal } from "../modals/TemplatedMailModal.tsx";

export function TemplateEditionForm(templateDto: Readonly<TemplateDto>) {
  const { updateTemplateMutation } = useTemplateMutation();
  const navigate = useNavigate();
  const { validateTemplateBodyLength } = useFormValidation();
  const location = useLocation();
  const previousPage = location.state?.pageNumber;
  const { customFormatHtml } = useFormatHtml();
  const [errors, setErrors] = useState<{
    body?: string;
  }>({});

  const initialDefaultValues = {
    id: templateDto.id,
    code: templateDto.code,
    subject: templateDto.defaultSubject ? templateDto.subject : templateDto.overrideSubject!,
    body: customFormatHtml(templateDto.defaultBody ? templateDto.body : templateDto.overrideBody!),
    overrideSubject: templateDto.overrideSubject,
    overrideBody: templateDto.overrideBody,
    variables: templateDto.variables
  };

  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch
  } = useForm({
    defaultValues: initialDefaultValues
  });

  const navigateBack = () => {
    navigate("/templates", { state: { pageNumber: previousPage } });
  };

  const cancelChanges = () => {
    reset(initialDefaultValues);
  };

  const onSubmit = (template: Template) => {
    if (customFormatHtml(currentBody) != customFormatHtml(initialDefaultValues.body)) {
      template.overrideBody = customFormatHtml(currentBody);
      template.body = customFormatHtml(template.body);
    }
    if (currentSubject != initialDefaultValues.subject) {
      template.overrideSubject = currentSubject;
    }
    updateTemplateMutation.mutate({
      template: template,
      source: "onSubmit"
    });
    reset(template);
  };

  unstable_usePrompt({
    when: isDirty,
    message: "You have unsaved changes, you will lose them if you proceed."
  });

  const beforeunload = (e: Event) => {
    if (isDirty) {
      e.preventDefault();
    }
  };

  const openResetToDefaultModal = (templateDto: TemplateDto, source: string) =>
    modals.openConfirmModal({
      title: "Warning",
      yOffset: rem(200),
      children: (
        <Text size="sm">
          Are you sure you want to come back to the default template's {source} ?
        </Text>
      ),
      labels: { confirm: "reset to default", cancel: "Cancel" },
      onConfirm: () => {
        updateTemplateMutation.mutate(
          {
            template: templateDto,
            source: "reset"
          },
          {
            onSuccess: (data) => {
              reset({
                ...data,
                subject: data.defaultSubject ? data.subject : data.overrideSubject,
                body: customFormatHtml(data.defaultBody ? data.body : data.overrideBody)
              });
            }
          }
        );
      }
    });

  const resetSubjectToDefault = (templateDto: TemplateDto) => {
    const updatedTemplate = { ...templateDto };
    updatedTemplate.overrideSubject = undefined;
    openResetToDefaultModal(updatedTemplate, "subject");
  };

  const resetBodyToDefault = (templateDto: TemplateDto) => {
    const updatedTemplate = { ...templateDto };
    updatedTemplate.overrideBody = undefined;
    openResetToDefaultModal(updatedTemplate, "body");
  };

  useEffect(() => {
    window.addEventListener("beforeunload", beforeunload);
    return () => {
      window.removeEventListener("beforeunload", beforeunload);
    };
  }, [isDirty]);

  const currentBody = watch("body");
  const currentSubject = watch("subject");

  const validateBodyField = (body: string, field: string) => {
    if (!validateTemplateBodyLength(body)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: "The template body exceeds 65 kB, please reduce it."
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: ""
      }));
    }
  };

  useEffect(() => {
    validateBodyField(currentBody, "body");
  }, [currentBody]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Title>
        <ActionIcon
          className={classes.icon}
          onClick={() => {
            isDirty ? CancelModal(navigateBack) : navigateBack();
          }}
        >
          <IconArrowLeft width="128" height="128" stroke="2" />
        </ActionIcon>
        Editing {templateDto.code}
      </Title>
      <Divider size="sm" />

      <Text className={classes.variables}>
        Variables:
        <ScrollArea.Autosize className={classes.scrollArea} mah={55} style={{ color: "gray" }}>
          {templateDto?.variables.length > 0
            ? templateDto?.variables.map((variable: string) => (
                <span className={classes.badge} key={variable}>
                  {variable}
                </span>
              ))
            : "None"}
        </ScrollArea.Autosize>
      </Text>
      <Text className={classes.editor}>
        Subject{" "}
        {templateDto.defaultSubject ? null : (
          <Button
            size="compact-xs"
            className={classes.reset}
            onClick={() => {
              resetSubjectToDefault(templateDto);
            }}
          >
            Reset subject to default
          </Button>
        )}
      </Text>
      <Textarea
        maxLength={MAX_VARCHAR_LENGTH}
        rows={1}
        placeholder="No subject"
        {...register("subject")}
      />
      <VisuallyHidden>
        <Textarea rows={1} hidden {...register("overrideSubject")} />
      </VisuallyHidden>
      <Text className={classes.editor}>
        Body{" "}
        {templateDto.defaultBody ? null : (
          <Button
            size="compact-xs"
            className={classes.reset}
            onClick={() => {
              resetBodyToDefault(templateDto);
            }}
          >
            Reset body to default
          </Button>
        )}
      </Text>
      <Textarea
        error={errors.body}
        rows={30}
        maxLength={MAX_TEXT_LENGTH}
        placeholder="No body"
        {...register("body")}
      />
      <VisuallyHidden>
        <Textarea {...register("overrideBody")} />
      </VisuallyHidden>
      <Box className={classes.button}>
        <Box>
          <TemplatePreviewModal
            id={templateDto.id}
            code={templateDto.code}
            variables={templateDto.variables}
            body={currentBody}
            subject={currentSubject}
          />
          <TemplatedMailModal
            id={templateDto.id}
            code={templateDto.code}
            subject={
              templateDto.defaultSubject ? templateDto.subject : templateDto.overrideSubject!
            }
            body={templateDto.defaultBody ? templateDto.body : templateDto.overrideBody!}
            variables={templateDto.variables}
          />
        </Box>
        <Box>
          {isDirty ? (
            <Button
              className={classes.cancel}
              onClick={() => {
                CancelModal(cancelChanges);
              }}
            >
              Cancel
            </Button>
          ) : null}
          {isDirty ? <Button type="submit">Save</Button> : null}
        </Box>
      </Box>
    </form>
  );
}
